import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { ImagePlus, X, Users, HeartHandshake, HandHeart, Gift } from "lucide-react";

const fileToDataUrl = (file) => new Promise((res, rej) => {
  const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
});

export default function ClubProfile() {
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const logoRef = useRef(); const coverRef = useRef(); const galRef = useRef();

  useEffect(() => {
    api.get("/clubs/categories").then((r) => setCats(r.data.categories || [])).catch(() => {});
    api.get("/clubs/me").then((r) => {
      const a = r.data.club;
      setForm({
        name: a.name || "", category: a.category || "autre", description: a.description || "",
        presentation: a.presentation || "", logo_url: a.logo_url || "", cover_url: a.cover_url || "",
        gallery: a.gallery || [], email: a.email || "", phone: a.phone || "", website: a.website || "",
        address: a.address || "", postal_code: a.postal_code || "", city: a.city || "",
        facebook_url: a.facebook_url || "", instagram_url: a.instagram_url || "",
        membership_info: a.membership_info || "", join_url: a.join_url || "", donation_url: a.donation_url || "", helloasso_url: a.helloasso_url || "",
        looking_for_volunteers: !!a.looking_for_volunteers,
      });
    }).catch(() => {});
  }, []);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const pickImage = async (ref, key) => {
    const f = ref.current?.files?.[0]; if (!f) return;
    if (f.size > 6 * 1024 * 1024) { toast.error("Image trop lourde (max 6 Mo)."); return; }
    set(key, await fileToDataUrl(f));
  };
  const addGallery = async () => {
    const files = Array.from(galRef.current?.files || []);
    const urls = [];
    for (const f of files.slice(0, 8)) { if (f.size <= 6 * 1024 * 1024) urls.push(await fileToDataUrl(f)); }
    set("gallery", [...(form.gallery || []), ...urls].slice(0, 8));
  };

  const save = async () => {
    if (!form.name || form.name.length < 2) { toast.error("Le nom est requis."); return; }
    setBusy(true);
    try {
      const { data } = await api.put("/clubs/me", form);
      toast.success("Page enregistrée !");
      set("gallery", data.club.gallery || []);
      set("logo_url", data.club.logo_url || "");
      set("cover_url", data.club.cover_url || "");
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur à l'enregistrement."); }
    finally { setBusy(false); }
  };

  if (!form) return <div className="p-6 text-slate-500">Chargement…</div>;

  return (
    <div className="max-w-3xl" data-testid="club-profile">
      <h1 className="font-heading text-2xl md:text-3xl font-extrabold">Ma page vitrine</h1>
      <p className="text-slate-500 text-sm mt-1">Ces informations composent votre page publique.</p>

      <div className="mt-6 space-y-6">
        {/* Images */}
        <section className="bg-white border border-slate-200 rounded-2xl p-5">
          <h2 className="font-heading font-bold mb-3">Visuels</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Logo</Label>
              <div className="mt-1 flex items-center gap-3">
                <div className="w-16 h-16 rounded-xl bg-slate-100 overflow-hidden grid place-items-center shrink-0">
                  {form.logo_url ? <img src={form.logo_url} alt="" className="w-full h-full object-cover" /> : <Users className="text-slate-300" />}
                </div>
                <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={() => pickImage(logoRef, "logo_url")} />
                <Button type="button" variant="outline" size="sm" onClick={() => logoRef.current?.click()} data-testid="club-logo-btn"><ImagePlus size={14} className="mr-1" /> Choisir</Button>
              </div>
            </div>
            <div>
              <Label>Image de couverture</Label>
              <div className="mt-1 flex items-center gap-3">
                <div className="w-28 h-16 rounded-xl bg-slate-100 overflow-hidden grid place-items-center shrink-0">
                  {form.cover_url ? <img src={form.cover_url} alt="" className="w-full h-full object-cover" /> : <ImagePlus className="text-slate-300" />}
                </div>
                <input ref={coverRef} type="file" accept="image/*" className="hidden" onChange={() => pickImage(coverRef, "cover_url")} />
                <Button type="button" variant="outline" size="sm" onClick={() => coverRef.current?.click()} data-testid="club-cover-btn"><ImagePlus size={14} className="mr-1" /> Choisir</Button>
              </div>
            </div>
          </div>
          <div className="mt-4">
            <Label>Galerie (jusqu'à 8 photos)</Label>
            <div className="mt-1 flex flex-wrap gap-2">
              {(form.gallery || []).map((g, i) => (
                <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden border border-slate-100">
                  <img src={g} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => set("gallery", form.gallery.filter((_, j) => j !== i))}
                          className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full p-0.5" data-testid={`club-gallery-remove-${i}`}><X size={12} /></button>
                </div>
              ))}
              <input ref={galRef} type="file" accept="image/*" multiple className="hidden" onChange={addGallery} />
              <button type="button" onClick={() => galRef.current?.click()} className="w-20 h-20 rounded-lg border-2 border-dashed border-slate-200 grid place-items-center text-slate-400 hover:border-indigo-400" data-testid="club-gallery-add"><ImagePlus size={18} /></button>
            </div>
          </div>
        </section>

        {/* Infos */}
        <section className="bg-white border border-slate-200 rounded-2xl p-5">
          <h2 className="font-heading font-bold mb-3">Présentation</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><Label>Nom *</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="club-profile-name" /></div>
            <div>
              <Label>Catégorie</Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="club-profile-category"><SelectValue /></SelectTrigger>
                <SelectContent>{cats.map((c) => (<SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div><Label>Ville</Label><Input value={form.city} onChange={(e) => set("city", e.target.value)} /></div>
            <div className="sm:col-span-2"><Label>Accroche courte</Label><Input value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Une phrase qui vous résume" /></div>
            <div className="sm:col-span-2"><Label>Présentation détaillée</Label><Textarea rows={5} value={form.presentation} onChange={(e) => set("presentation", e.target.value)} /></div>
          </div>
        </section>

        {/* Contacts */}
        <section className="bg-white border border-slate-200 rounded-2xl p-5">
          <h2 className="font-heading font-bold mb-3">Coordonnées & réseaux</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Email de contact</Label><Input value={form.email} onChange={(e) => set("email", e.target.value)} /></div>
            <div><Label>Téléphone</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} /></div>
            <div><Label>Site internet</Label><Input value={form.website} onChange={(e) => set("website", e.target.value)} placeholder="https://…" /></div>
            <div><Label>Facebook</Label><Input value={form.facebook_url} onChange={(e) => set("facebook_url", e.target.value)} placeholder="https://facebook.com/…" /></div>
            <div><Label>Instagram</Label><Input value={form.instagram_url} onChange={(e) => set("instagram_url", e.target.value)} placeholder="https://instagram.com/…" /></div>
            <div><Label>Adresse</Label><Input value={form.address} onChange={(e) => set("address", e.target.value)} /></div>
            <div><Label>Code postal</Label><Input value={form.postal_code} onChange={(e) => set("postal_code", e.target.value)} /></div>
          </div>
        </section>

        {/* Adhésion */}
        <section className="bg-white border border-slate-200 rounded-2xl p-5">
          <h2 className="font-heading font-bold mb-3 flex items-center gap-2"><HeartHandshake size={18} className="text-indigo-700" /> Adhésion</h2>
          <div className="space-y-3">
            <div><Label>Informations d'adhésion</Label><Textarea rows={3} value={form.membership_info} onChange={(e) => set("membership_info", e.target.value)} placeholder="Cotisation, modalités d'inscription, période…" data-testid="club-res-membership" /></div>
            <div><Label>Lien d'adhésion / contact</Label><Input value={form.join_url} onChange={(e) => set("join_url", e.target.value)} placeholder="https://… ou mailto:" data-testid="club-res-joinurl" /></div>
          </div>
        </section>

        {/* Bénévoles */}
        <section className="bg-white border border-slate-200 rounded-2xl p-5">
          <h2 className="font-heading font-bold mb-3 flex items-center gap-2"><HandHeart size={18} className="text-indigo-700" /> Appel aux bénévoles</h2>
          <div className="flex items-center justify-between">
            <div>
              <Label className="mb-0">Nous recherchons des bénévoles</Label>
              <p className="text-xs text-slate-500 mt-1">Affiche un bouton de contact sur votre page publique.</p>
            </div>
            <Switch checked={form.looking_for_volunteers} onCheckedChange={(v) => set("looking_for_volunteers", v)} data-testid="club-res-volunteers" />
          </div>
        </section>

        {/* Dons */}
        <section className="bg-white border border-slate-200 rounded-2xl p-5">
          <h2 className="font-heading font-bold mb-3 flex items-center gap-2"><Gift size={18} className="text-indigo-700" /> Dons</h2>
          <div><Label>Lien de don</Label><Input value={form.donation_url} onChange={(e) => set("donation_url", e.target.value)} placeholder="https://… (HelloAsso, cagnotte…)" data-testid="club-res-donation" /></div>
          <div><Label>Page HelloAsso (adhésion / billetterie)</Label><Input value={form.helloasso_url} onChange={(e) => set("helloasso_url", e.target.value)} placeholder="https://www.helloasso.com/associations/votre-club" data-testid="club-res-helloasso" /><p className="text-xs text-slate-500 mt-1">Un bouton « Adhérer / Billetterie » apparaîtra sur votre vitrine publique.</p></div>
          <p className="text-xs text-slate-500 mt-2">Collez le lien de votre plateforme de dons (HelloAsso, cagnotte en ligne, etc.). Le bouton « Faire un don » apparaîtra alors sur votre page publique.</p>
        </section>

        <div className="flex justify-end sticky bottom-0 bg-slate-50 py-3">
          <Button onClick={save} disabled={busy} className="btn-shine text-white" data-testid="club-profile-save">{busy ? "Enregistrement…" : "Enregistrer ma page"}</Button>
        </div>
      </div>
    </div>
  );
}
